import { Injectable, Injector, inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Observable, catchError, lastValueFrom, throwError, map, tap } from 'rxjs';
import { TuiDialogService } from '@taiga-ui/core';
import { PolymorpheusComponent } from '@taiga-ui/polymorpheus';
import { TranslateService } from '@ngx-translate/core';
import { Optional } from '@src/types/utils';
import {
  FileInfoBase,
  LoyaltyCodesForProductService,
  LoyaltyDigestSendTo,
  LoyaltyFavoriteProductAddRequestDto,
  LoyaltyFavoriteProductService,
  LoyaltyProductCode,
  LoyaltyProductWCategiriesEdit,
  LoyaltyService,
} from '@src/api';
import { AlertService, DocumentService } from '@src/core/services';
import { PhotoService } from '@src/app/modules/photo';

import { ConfirmDialogComponent } from '../ui';
import { LoyaltyProgramProduct, LoyaltyProgramProductImage, LoyaltyProgramProductInput } from '../models';
import { EnvService } from '../../env';

import { convertFromProductInputAdd, convertFromProductInputEdit } from './utils/convertFromProductInput';
import { convertToProduct } from './utils/convertToProduct';
import { QrCodeImageFile, QrCodeImageIndexId, UpdateQrCodeImagesResult } from './types';
import { LpViewProductsStoreService } from './store';

@Injectable({
  providedIn: 'root',
})
export class LoyaltyProgramApiService {
  private readonly api = inject(LoyaltyService);
  private readonly loyaltyFavoriteProductService = inject(LoyaltyFavoriteProductService);
  private readonly alertService = inject(AlertService);
  private readonly photoService = inject(PhotoService);
  private readonly translateService = inject(TranslateService);
  private readonly loyaltyCodesForProductService = inject(LoyaltyCodesForProductService);
  private readonly documentService = inject(DocumentService);
  private readonly env = inject(EnvService);
  private readonly dialogService = inject(TuiDialogService);
  private readonly injector = inject(Injector);
  private readonly lpViewProductsStoreService = inject(LpViewProductsStoreService);

  getProductById(id: string, unionId?: string, collectionId?: string, ref?: string) {
    return this.api.loyaltyProduct(id, unionId, collectionId, ref).pipe(
      catchError(err => {
        this.showError(this.translateService.instant('components.loyaltyProgram.alerts.errors.loyaltyProduct'), err);
        return throwError(() => err);
      }),
      map(result => convertToProduct(result)),
      tap(product => {
        this.lpViewProductsStoreService.setData(product.id, product);
      }),
    );
  }

  async createProduct(product: LoyaltyProgramProductInput): Promise<string | void> {
    let newImages: FileInfoBase[] = [];

    // Массив идентификаторов изображений QR кодов, которые загрузил пользователь сам
    let qrCodeImages: QrCodeImageIndexId = [];

    try {
      newImages = await this.uploadImages(product.images, undefined, product.unionId);
      qrCodeImages = await this.createQrCodeImages(product);
      await this.uploadUniqueCodes(product);

      const { id } = await lastValueFrom(
        this.api.addLoyaltyProduct(
          convertFromProductInputAdd({
            product,
            imagesIds: newImages.map(item => item.id!),
            coverImageId: this.getCoverImageId(product, newImages),
            qrCodeImages,
          }),
        ),
      );

      if (!id) {
        this.deleteImages(newImages);
        this.deleteImages(qrCodeImages);
        this.alertService.error(
          this.translateService.instant('components.loyaltyProgram.alerts.errors.addLoyaltyProduct'),
        );
        return;
      }

      return id;
    } catch (err) {
      this.deleteImages(newImages);
      this.deleteImages(qrCodeImages);
      this.showError(this.translateService.instant('components.loyaltyProgram.alerts.errors.addLoyaltyProduct'), err);
      throw err;
    }
  }

  async updateProduct(product: LoyaltyProgramProductInput, origin?: LoyaltyProgramProduct): Promise<string | void> {
    if (!origin?.id) {
      return;
    }

    // Список картинок, которые были ранее загружены
    const existsImageIds = product.images?.filter(item => !!item.id).map(item => item.id!) ?? [];
    // Новые, которые хотим загрузить
    const newImageIds = product.images?.filter(item => !item.id) ?? [];
    // Список, который нужно удалить, т.к. пользователь их удалил при редактировании
    const imageIdsToDelete = origin.images
      ?.map<string>(item => item.id!)
      .filter(imageId => !existsImageIds.includes(imageId));

    let newImages: FileInfoBase[] = [];
    let newQrCodeImages: FileInfoBase[] = [];

    try {
      // Грузим новые картинки без обложки
      newImages = await this.uploadImages(newImageIds, false, product.unionId);
      const updateResult = await this.updateQrCodeImages(product, origin);
      await this.uploadUniqueCodes(product);

      newQrCodeImages = updateResult.newQrCodeImages;

      const payload: LoyaltyProductWCategiriesEdit = {
        ...convertFromProductInputEdit({
          product,
          imagesIds: [...existsImageIds, ...newImages.map(item => item.id!)],
          coverImageId: this.getCoverImageId(product, newImages),
          qrCodeImages: updateResult.qrCodeImages,
        }),
        id: origin.id,
      };

      const { id } = await lastValueFrom(this.api.editLoyaltyProduct(payload));

      if (!id) {
        this.deleteImages(newImages);
        this.deleteImages(newQrCodeImages);
        this.alertService.error(
          this.translateService.instant('components.loyaltyProgram.alerts.errors.editLoyaltyProduct'),
        );
        return;
      }

      await Promise.all([this.deleteImages(imageIdsToDelete), this.deleteImages(updateResult.qrCodeImageIdsToDelete)]);

      return id;
    } catch (err) {
      this.deleteImages(newImages);
      this.deleteImages(newQrCodeImages);
      this.showError(this.translateService.instant('components.loyaltyProgram.alerts.errors.editLoyaltyProduct'), err);
      throw err;
    }
  }

  deleteProduct(id?: string): Promise<boolean> {
    return new Promise<boolean>(resolve => {
      if (!id) {
        return resolve(false);
      }

      this.dialogService
        .open<void | boolean>(new PolymorpheusComponent(ConfirmDialogComponent, this.injector), {
          label: this.translateService.instant('common.dialogs.deletingEntryHeader'),
          data: this.translateService.instant('common.dialogs.deletingProductConfirmLabel'),
        })
        .subscribe(result => {
          if (result) {
            lastValueFrom(this.api.removeLoyaltyProduct(id))
              .then(() => {
                resolve(true);
              })
              .catch(err => {
                this.showError(
                  this.translateService.instant('components.loyaltyProgram.alerts.errors.removeLoyaltyProduct'),
                  err,
                );
                resolve(false);
              });
          } else {
            resolve(false);
          }
        });
    });
  }

  async publishProduct(id?: string): Promise<boolean> {
    if (!id) {
      return false;
    }

    try {
      await lastValueFrom(this.api.activateLoyaltyProduct(id));
    } catch (err) {
      this.showError(
        this.translateService.instant('components.loyaltyProgram.alerts.errors.activateLoyaltyProduct'),
        err,
      );
      return false;
    }

    return true;
  }

  async unpublishProduct(id?: string): Promise<boolean> {
    if (!id) {
      return false;
    }

    try {
      await lastValueFrom(this.api.deactivateLoyaltyProduct(id));
    } catch (err) {
      this.showError(
        this.translateService.instant('components.loyaltyProgram.alerts.errors.deactivateLoyaltyProduct'),
        err,
      );
      return false;
    }

    return true;
  }

  getProductCode(productGroupId: string, unionId?: string): Observable<LoyaltyProductCode> {
    return this.loyaltyCodesForProductService.codeByGroupId(productGroupId, unionId).pipe(
      catchError(err => {
        this.alertService.error(err);
        return throwError(() => err);
      }),
    );
  }

  downloadCodesByGroupId(productGroupId?: string, unionId?: string) {
    if (!productGroupId) {
      this.alertService.error(
        this.translateService.instant('components.loyaltyProgram.alerts.errors.noProductGroupId'),
      );
      return;
    }

    this.loyaltyCodesForProductService
      .exportCodesByGroupId(productGroupId, unionId, 'response')
      .pipe(
        catchError(err => {
          this.alertService.error(err);
          return throwError(() => err);
        }),
      )
      .subscribe(response => {
        this.env.downloadFileByHttpResponse(response);
      });
  }

  deleteCodesByGroupId(productGroupId?: string) {
    if (!productGroupId) {
      this.alertService.error(
        this.translateService.instant('components.loyaltyProgram.alerts.errors.noProductGroupId'),
      );
      return;
    }

    this.dialogService
      .open<void | boolean>(new PolymorpheusComponent(ConfirmDialogComponent, this.injector), {
        label: this.translateService.instant('common.dialogs.deletingCodesHeader'),
        data: this.translateService.instant('common.dialogs.deletingCodesText'),
      })
      .subscribe(result => {
        if (result) {
          this.loyaltyCodesForProductService
            .removeCodesFromDb({ productGroupId })
            .pipe(
              catchError(err => {
                this.alertService.error(err);
                return throwError(() => err);
              }),
            )
            .subscribe(() => {
              this.alertService.success(
                this.translateService.instant('components.loyaltyProgram.alerts.successes.removeCodes'),
              );
            });
        }
      });
  }

  addProductToFavorite(product: LoyaltyFavoriteProductAddRequestDto) {
    return this.loyaltyFavoriteProductService.apiLoyaltyProductFavoritesAddPost(product).pipe(
      catchError(err => {
        this.alertService.error(err);
        return throwError(() => err);
      }),
    );
  }

  removeProductFromFavorite(product: LoyaltyFavoriteProductAddRequestDto) {
    return this.loyaltyFavoriteProductService.apiLoyaltyProductFavoritesRemovePost(product).pipe(
      catchError(err => {
        this.alertService.error(err);
        return throwError(() => err);
      }),
    );
  }

  saveRedirectLink(productId: string, link?: string, unionId?: string) {
    this.api.saveRedirectLink(productId, link, unionId).subscribe();
  }

  sendDigest(data: LoyaltyDigestSendTo = {}) {
    return this.api.sendDigest(data);
  }

  digestTest() {
    return this.api.digestTest();
  }

  private showError(label: string, err: unknown) {
    this.alertService.error(err as HttpErrorResponse, { label, autoClose: 0 }); // TODO: исправить тип
  }

  private getCoverImageId(product: LoyaltyProgramProductInput, newImages: FileInfoBase[]): Optional<string> {
    const coverImage = product.images?.find(item => item.isCover);

    if (coverImage?.id) {
      // Уже существующая запись
      return coverImage.id;
    } else if (coverImage?.file instanceof File) {
      // Надо найти по имени файла id из вновь созданных
      return newImages.find(item => item.fileName === (coverImage.file as File).name)?.id;
    }

    return undefined;
  }

  private async deleteImages(images?: Array<undefined | string | FileInfoBase>) {
    await Promise.all(
      images
        ?.filter(item => !!item)
        .forEach(image => {
          return lastValueFrom(this.photoService.deletePhoto(typeof image === 'string' ? image : image?.id!));
        }) ?? [],
    );
  }

  /**
   * Функция загрузки файлов, которые относятся к нашему продукту
   * @param images набор файлов, которые были заполнены на форме
   * @param isAutoCover флаг, что автоматически обновляем обложку
   */
  private async uploadImages(
    images?: LoyaltyProgramProductImage[],
    isAutoCover = true,
    unionId?: string,
  ): Promise<FileInfoBase[]> {
    if (!(images && images.length)) {
      return [];
    }

    // Новые файлы, которые пользователь загрузил
    let newFiles: File[] = [];
    let coverFile: Optional<File>;

    images.forEach(item => {
      if (item.file instanceof File) {
        if (item.isCover && isAutoCover) {
          coverFile = item.file;
        } else {
          newFiles.push(item.file);
        }
      }
    });

    if (!(newFiles.length || coverFile)) {
      return [];
    }

    if (coverFile) {
      newFiles = [coverFile, ...newFiles];
    }

    const { files = [] } = await lastValueFrom(
      this.photoService.uploadFiles(newFiles, undefined, isAutoCover, undefined, unionId),
    );

    return files;
  }

  private getQrCodeImageIndexIds(product: LoyaltyProgramProductInput): [QrCodeImageIndexId, QrCodeImageFile[]] {
    const result: QrCodeImageIndexId = [];
    const newFiles: QrCodeImageFile[] = [];

    product.terms.forEach((term, index) => {
      const { qrCodeImage } = term;

      if (qrCodeImage?.id) {
        result[index] = qrCodeImage.id;
      } else if (qrCodeImage?.file instanceof File) {
        newFiles.push({
          file: qrCodeImage.file,
          index,
        });
      }
    });

    return [result, newFiles];
  }

  private async uploadUniqueCodes(product: LoyaltyProgramProductInput): Promise<string[]> {
    return await Promise.all(
      product.terms.map(async term => {
        if (!term.uniqueCodes || !term.uniqueCodes.file || typeof term.uniqueCodes.file === 'string') return '';

        const newDocument = await lastValueFrom(this.documentService.addDocuments([term.uniqueCodes.file]));
        term.uniqueCodes.id = newDocument.files?.[0].id;
        return term.uniqueCodes.id ?? '';
      }),
    );
  }

  private async createQrCodeImages(product: LoyaltyProgramProductInput): Promise<QrCodeImageIndexId> {
    const [result, newFiles] = this.getQrCodeImageIndexIds(product);

    if (newFiles.length) {
      const { files = [] } = await lastValueFrom(
        this.photoService.uploadFiles(
          newFiles.map(item => item.file),
          undefined,
          false,
          undefined,
          product.unionId,
        ),
      );

      files.forEach(({ id }, index) => {
        result[newFiles[index].index] = id;
      });
    }

    return result;
  }

  private async updateQrCodeImages(
    product: LoyaltyProgramProductInput,
    origin: LoyaltyProgramProductInput,
  ): Promise<UpdateQrCodeImagesResult> {
    const [qrCodeImages, newFiles] = this.getQrCodeImageIndexIds(product);

    let newQrCodeImages: FileInfoBase[] = [];

    if (newFiles.length) {
      const { files = [] } = await lastValueFrom(
        this.photoService.uploadFiles(
          newFiles.map(item => item.file),
          undefined,
          false,
          undefined,
          product.unionId,
        ),
      );

      newQrCodeImages = files;
      files.forEach(({ id }, index) => {
        qrCodeImages[newFiles[index].index] = id;
      });
    }

    // Вычисляем идентификаторы, которые нужно удалить
    const qrCodeImageIdsToDelete: string[] = [];

    origin.terms.forEach(term => {
      const qrCodeImageId = term.qrCodeImage?.id;

      if (qrCodeImageId && !qrCodeImages.includes(qrCodeImageId)) {
        qrCodeImageIdsToDelete.push(qrCodeImageId);
      }
    });

    return {
      qrCodeImageIdsToDelete,
      qrCodeImages,
      newQrCodeImages,
    };
  }
}
